import React, { useEffect, useMemo } from 'react';
import { Card, Container, Row, Col, Badge, Accordion } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import elearningApi from 'api/elearningApi';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatTime } from 'utils/commonUtils';
import QuestionItem from '../components/QuestionItem';
import ClozeQuestionItem from '../components/ClozeQuestionItem';
import { QUIZ_GRADE_STATUS } from 'constants/driving-elearning.constant';
import BackButton from 'components/BackButton';
import { parseQuestionHTML } from 'utils/elearning.utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectElearningData,
  selectElearningSettings,
  selectQuizzes,
  updateElearningData,
} from 'store/elearning.slice';
import TimeExceedWarning from '../components/TimeExceedWarning';
import { MdAccessTime, MdChecklist, MdOutlineQuiz, MdSchedule } from 'react-icons/md';

const formatDateTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  return date.toLocaleString('en-GB');
};

const ElearningAttemptResultPage = () => {
  const dispatch = useDispatch();
  const { isLimitExceeded, timeLimitPerDay, totalTodayTime } = useSelector(selectElearningData);
  const elearningSettings = useSelector(selectElearningSettings);
  const quizzesMap = useSelector(selectQuizzes);
  const attemptId = useParams().attemptId;
  const courseId = new URLSearchParams(window.location.search).get('c');
  const courseIdNum = courseId ? Number(courseId) : null;
  const cachedQuizzes = courseIdNum ? quizzesMap?.[courseIdNum] : null;
  const [data, setData] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const { grade, attempt, questions } = data;
  const [quizzes, setQuizzes] = React.useState(cachedQuizzes || []);
  const quiz = useMemo(() => {
    if (!quizzes.length) return null;
    const quizId = attempt?.quiz;
    return quizzes.find((quiz) => quiz.id === quizId);
  }, [quizzes, attempt]);

  const timePerQuestion = elearningSettings?.[courseId]?.timePerQuestionInMinute ?? 0.5;

  const answeredCount = useMemo(() => {
    if (!questions) return 0;
    return questions.filter((q) => q.state !== 'gaveup' && q.state !== 'notyetanswered').length;
  }, [questions]);

  const accumulatedTime = answeredCount * timePerQuestion * 60;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Skip the per-course quizzes fetch when Redux already has it.
        const quizzesPromise = cachedQuizzes
          ? Promise.resolve(cachedQuizzes)
          : elearningApi.quizzesByCourses([courseId]);
        const [attemptRes, quizzesRes] = await Promise.all([
          elearningApi.quizGetAttemptReview(attemptId),
          quizzesPromise,
        ]);
        setData(attemptRes);
        if (Array.isArray(quizzesRes)) {
          setQuizzes(quizzesRes);
          if (!cachedQuizzes && courseIdNum) {
            dispatch(
              updateElearningData({
                quizzes: { ...(quizzesMap || {}), [courseIdNum]: quizzesRes },
              })
            );
          }
        }
      } catch (error) {
        console.error('Error fetching attempt result:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // quizzesMap/cachedQuizzes intentionally omitted — fetch once per attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  if (isLimitExceeded) {
    return <TimeExceedWarning timeLimitPerDay={timeLimitPerDay} totalTodayTime={totalTodayTime} />;
  }

  if (!data?.attempt) {
    return loading ? <LoadingSpinner /> : <div>Không có dữ liệu</div>;
  }

  return (
    <div style={{ height: '100vh', overflowY: 'scroll' }}>
      <Container className='my-2'>
        <BackButton />

        <Card className='mb-4 shadow-sm'>
          <Card.Body>
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <h5 className='mb-0'>{quiz?.name}</h5>
              {quiz?.timelimit > 0 && (
                <Badge
                  bg={grade >= quiz?.gradepass ? 'success' : 'danger'}
                  style={{ fontSize: '0.85rem', padding: '6px 12px' }}
                >
                  {grade >= quiz?.gradepass ? 'Đạt' : 'Không đạt'}
                </Badge>
              )}
            </div>

            <Row className='g-3'>
              <Col xs={6} md={3}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  <MdOutlineQuiz className='me-1' />Số câu đúng
                </div>
                <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {attempt.sumgrades}/{questions.length}
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  <MdAccessTime className='me-1' />Thời gian tích luỹ
                </div>
                <div style={{ fontWeight: 600, fontSize: '1rem', color: '#059669' }}>
                  {formatTime(accumulatedTime)}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                  {answeredCount} câu × {timePerQuestion} phút/câu
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  <MdSchedule className='me-1' />Bắt đầu
                </div>
                <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                  {formatDateTime(attempt.timestart)}
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  <MdChecklist className='me-1' />Kết thúc
                </div>
                <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                  {formatDateTime(attempt.timefinish)}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Accordion defaultActiveKey='0' className='mb-4'>
          {questions.map((q, index) => (
            <Accordion.Item eventKey={String(index)} key={q.slot}>
              <Accordion.Header>
                <div>
                  Câu hỏi {q.number}{' '}
                  {quiz?.sumgrades - quiz?.gradepass < q?.maxmark && (
                    <span className='text-danger fw-bold'>(Câu điểm liệt)</span>
                  )}
                </div>
                <Badge
                  bg={
                    q?.state === 'gradedright'
                      ? 'success'
                      : q?.state === 'gradedwrong'
                      ? 'danger'
                      : q?.state === 'gradedpartial'
                      ? 'warning'
                      : q?.state === 'gaveup'
                      ? 'warning'
                      : ''
                  }
                  className='ms-2'
                >
                  {QUIZ_GRADE_STATUS[q?.state]}
                </Badge>
              </Accordion.Header>
              <Accordion.Body>
                {(() => {
                  // Câu cloze (mô phỏng) render bằng component riêng
                  const parsed = parseQuestionHTML(q.html);
                  const Item = parsed?.type === 'multianswer' ? ClozeQuestionItem : QuestionItem;
                  return <Item question={parsed} key={index} disabled={true} />;
                })()}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Container>
    </div>
  );
};

export default ElearningAttemptResultPage;
